import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

// Axios instance for inquiries API
const inquiriesAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: warehouseParamsSerializer,
});

// Attach auth token if available
inquiriesAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;
    return applyWarehouseFilter(config);
  },
  (error) => Promise.reject(error)
);

// Redirect on auth failure
inquiriesAPI.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

const BASE_URL = '/inquiries';

// Caching helpers (memory + sessionStorage)
const CACHE_PREFIX = 'erp_inquiries_cache_v1';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

const memoryCache = new Map();

function cacheKey(key) {
  return `${CACHE_PREFIX}::${key}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires) return null;
    if (Date.now() > parsed.expires) {
      sessionStorage.removeItem(cacheKey(key));
      return null;
    }
    return parsed.value;
  } catch (e) {
    return null;
  }
}

function writeCache(key, value, ttl = DEFAULT_TTL_MS) {
  try {
    sessionStorage.setItem(cacheKey(key), JSON.stringify({ value, expires: Date.now() + ttl }));
  } catch (e) { }
}

function readMemoryCache(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expires) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}

function writeMemoryCache(key, value, ttl = DEFAULT_TTL_MS) {
  memoryCache.set(key, { value, expires: Date.now() + ttl });
}

function deleteCacheEntry(key) {
  try { memoryCache.delete(key); } catch (e) { }
  try { sessionStorage.removeItem(cacheKey(key)); } catch (e) { }
}

function updateFullListCachesWithModifier(modifierFn) {
  try {
    for (const [key, entry] of memoryCache.entries()) {
      if (typeof key === 'string' && key.startsWith('all::')) {
        const arr = entry?.value;
        if (Array.isArray(arr)) writeMemoryCache(key, modifierFn(arr.slice()));
      }
    }
  } catch (e) { }

  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const sk = sessionStorage.key(i);
      if (!sk) continue;
      if (sk.startsWith(`${CACHE_PREFIX}::all::`)) {
        const raw = sessionStorage.getItem(sk);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          const val = parsed?.value;
          if (Array.isArray(val)) {
            parsed.value = modifierFn(val.slice());
            sessionStorage.setItem(sk, JSON.stringify(parsed));
          }
        } catch (e) { }
      }
    }
  } catch (e) { }
}


const inquiriesService = {
  async getAllInquiries(params = {}, options = {}) {
    const { force = false } = options;
    const key = `all::${JSON.stringify(params || {})}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }
    }

    try {
      const queryParams = new URLSearchParams();
      const page = params.page || 1;
      const limit = params.limit || 20;
      const skip = params.skip !== undefined ? params.skip : (page - 1) * limit;
      queryParams.append('skip', skip);
      queryParams.append('limit', limit);
      if (params.search !== undefined && params.search !== '') {
        queryParams.append('search', params.search);
      }
      if (params.from_date) queryParams.append('from_date', params.from_date);
      if (params.to_date) queryParams.append('to_date', params.to_date);
      const response = await inquiriesAPI.get(`${BASE_URL}?${queryParams.toString()}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async getInquiryById(inquiryId, options = {}) {
    const { force = false } = options;
    const key = `byId::${inquiryId}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }
      // fallback: search in a cached full-list
      const fullKey = `all::${JSON.stringify({})}`;
      const fullMem = readMemoryCache(fullKey) || readCache(fullKey);
      if (fullMem && Array.isArray(fullMem)) {
        const found = fullMem.find((i) => String(i.id) === String(inquiryId));
        if (found) { writeMemoryCache(key, found); writeCache(key, found); return { success: true, data: found }; }
      }
    }

    try {
      const response = await inquiriesAPI.get(`${BASE_URL}/${inquiryId}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  /**
   * Create an inquiry. The API may accept either an existing customer via customer_id
   * or a nested `customer` object for creating a new customer along with the inquiry.
   */
  async createInquiry(inquiryPayload) {
    // Route create request to appropriate backend endpoint depending on payload
    try {
      let response;

      // If payload contains a nested `customer` object -> create for new customer
      if (inquiryPayload && typeof inquiryPayload === 'object' && inquiryPayload.customer) {
        response = await inquiriesAPI.post(`${BASE_URL}/new-customer`, inquiryPayload);
      } else if (inquiryPayload && (inquiryPayload.customer_id || inquiryPayload.customerId)) {
        // If payload references an existing customer id -> use existing-customer endpoint
        response = await inquiriesAPI.post(`${BASE_URL}/existing-customer`, inquiryPayload);
      } else {
        // Fallback to generic create endpoint
        response = await inquiriesAPI.post(BASE_URL, inquiryPayload);
      }

      const created = response.data;

      // prepend to cached full-lists
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });

      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }

      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // Convenience methods if callers want to call explicit endpoints
  async createInquiryForNewCustomer(payload) {
    try {
      const response = await inquiriesAPI.post(`${BASE_URL}/new-customer`, payload);
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createInquiryForExistingCustomer(payload) {
    try {
      const response = await inquiriesAPI.post(`${BASE_URL}/existing-customer`, payload);
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async updateInquiry(inquiryId, inquiryPayload) {
    try {
      const response = await inquiriesAPI.put(`${BASE_URL}/${inquiryId}`, inquiryPayload);
      const updated = response.data;

      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(inquiryId) ? updated : i)));

      try { const key = `byId::${inquiryId}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }

      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  /**
   * Update inquiry status. Expects backend endpoint PUT /inquiries/:id/status with body { status: 'new_status' }
   */
  async updateInquiryStatus(inquiryId, statusPayload) {
    try {
      let response;

      if (typeof statusPayload === 'string') {
        response = await inquiriesAPI.patch(`${BASE_URL}/${inquiryId}/status`, null, { params: { new_status: statusPayload } });
      } else {
        // If it's an object, send it as request body. 
        // We also extract status for query param if needed by some backend patterns, 
        // but the main goal is to send the full payload in the body.
        const status = statusPayload?.status || statusPayload?.new_status || statusPayload?.newStatus;
        response = await inquiriesAPI.patch(`${BASE_URL}/${inquiryId}/status`, statusPayload, {
          params: status ? { new_status: status } : {}
        });
      }

      const updated = response.data;
      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(inquiryId) ? updated : i)));
      try { const key = `byId::${inquiryId}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async deleteInquiry(inquiryId) {
    try {
      const response = await inquiriesAPI.delete(`${BASE_URL}/${inquiryId}`);
      updateFullListCachesWithModifier((arr) => arr.filter((i) => String(i.id) !== String(inquiryId)));
      try { deleteCacheEntry(`byId::${inquiryId}`); } catch (e) { }
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async bulkDeleteInquiries(inquiryIds = []) {
    try {
      const ids = (inquiryIds || []).map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
      const response = await inquiriesAPI.post(`${BASE_URL}/bulk-delete`, ids);

      const idSet = new Set(ids.map((id) => String(id)));
      updateFullListCachesWithModifier((arr) => arr.filter((i) => !idSet.has(String(i.id))));
      try { for (const id of ids) deleteCacheEntry(`byId::${id}`); } catch (e) { }

      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async sendQuoteEmail(inquiryId) {
    try {
      const response = await inquiriesAPI.post(`${BASE_URL}/${inquiryId}/send-email`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async sendQuoteNotification(inquiryId, type = 'email') {
    try {
      const response = await inquiriesAPI.post(`${BASE_URL}/${inquiryId}/send-notification`, null, { params: { type } });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  /**
   * Download the quotation PDF — the same document the email and WhatsApp
   * channels attach, produced by the same generator, so what staff download is
   * exactly what the customer receives.
   *
   * Read-only: this records no send history, unlike sendQuoteNotification.
   */
  async downloadQuotationPdf(inquiryId) {
    try {
      const response = await inquiriesAPI.get(
        `${BASE_URL}/${inquiryId}/quotation-pdf`,
        { responseType: 'blob' },
      );
      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      // An error body arrives as a Blob because of responseType, so the usual
      // extractApiError sees no JSON. Read it back before falling through.
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          return { success: false, error: parsed?.error?.message || parsed?.detail || 'Failed to download PDF' };
        } catch (e) {
          /* not JSON — fall through to the generic extractor */
        }
      }
      return { success: false, error: extractApiError(error) };
    }
  },

  clearCache() {
    try {
      for (const [key] of memoryCache.entries()) {
        if (typeof key === 'string' && key.includes('inquiries')) memoryCache.delete(key);
      }
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k);
      }
    } catch (e) { }
  }
};

export default inquiriesService;
